import { useState } from "react";
import { api } from "../../api/apiClient";
import type { InvoiceDetail } from "../../api/types";
import { calendarDayText, money, percentText } from "../../i18n/intl";
import { businessToday } from "../../utils/businessDay";
import { notCountedText } from "../../utils/paperList";
import { isCreditNote } from "../../utils/paperUse";
import { parseTypedAmount } from "../../utils/typedAmount";

type RateDraft = { key: number; rate: string; base: string; iva: string };

let nextKey = 1;
const blankRate = (): RateDraft => ({ key: nextKey++, rate: "", base: "", iva: "" });

/**
 * G-173: a credit note's amounts are stored negative and typed positive (the
 * server gives the sign), so its fields open on the amount without the minus.
 */
function typedForm(value: string | null, creditNote: boolean): string {
  if (value === null) return "";
  return creditNote ? value.replace(/^-/, "") : value;
}

function ratesFrom(invoice: InvoiceDetail): RateDraft[] {
  const credit = isCreditNote(invoice.paperUse);
  if (invoice.vatBreakdown.length === 0) {
    return [
      {
        ...blankRate(),
        base: typedForm(invoice.baseEur, credit),
        iva: typedForm(invoice.ivaEur, credit),
      },
    ];
  }
  return invoice.vatBreakdown.map((r) => ({
    key: nextKey++,
    rate: percentText(r.ratePercent),
    base: typedForm(r.baseEur, credit),
    iva: typedForm(r.ivaEur, credit),
  }));
}

/** A typed rate: "21", "21,5", "10 %". */
function parseRate(raw: string): number | null {
  const s = raw.trim().replace(/\s*%$/, "").replace(",", ".");
  if (!/^\d{1,3}(?:\.\d{1,2})?$/.test(s)) return null;
  const n = Number(s);
  return n >= 0 && n <= 100 ? n : null;
}

/**
 * G-164: a factura's number, date, and base and IVA per rate — and the way to
 * type them when the reader missed or misread them (owner 2026-10-16 and
 * 2026-10-19). A factura without a date, a number or its IVA is never
 * counted; the "Not counted: …" pill says which. Whoever may write the paper
 * (the owner, or that shop's manager); the server holds the rules
 * (`paper-rules.ts` `assertFacturaDetailsEdit`, `typedFacturaMoney` — the
 * total is base + IVA, worked out there) and audits every change.
 */
export function FacturaDetailsBlock({
  invoice,
  onChanged,
}: {
  invoice: InvoiceDetail;
  /** Called after a save; the page reloads the paper and its preview. */
  onChanged: () => void;
}) {
  const savedDate = invoice.invoiceDate ? invoice.invoiceDate.slice(0, 10) : "";
  const [editing, setEditing] = useState(false);
  const [number, setNumber] = useState(invoice.invoiceNumber ?? "");
  const [date, setDate] = useState(savedDate);
  const [rates, setRates] = useState<RateDraft[]>(() => ratesFrom(invoice));
  const [moneyTouched, setMoneyTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = businessToday().ymd;
  const notCounted = notCountedText(invoice.countability);
  const complete = Boolean(invoice.invoiceNumber && savedDate && invoice.ivaEur !== null);
  // G-173: a credit note's details are typed here too; its amounts are what it credits.
  const credit = isCreditNote(invoice.paperUse);
  const paperWord = credit ? "credit note" : "factura";

  const open = () => {
    setNumber(invoice.invoiceNumber ?? "");
    setDate(savedDate);
    setRates(ratesFrom(invoice));
    setMoneyTouched(false);
    setError(null);
    setEditing(true);
  };

  const patchRate = (key: number, patch: Partial<RateDraft>) => {
    setMoneyTouched(true);
    setRates((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const save = async () => {
    const nextNumber = number.trim();
    // Neither can be cleared; either may stay missing while only the money is typed.
    if ((!nextNumber && invoice.invoiceNumber) || (!date && savedDate)) {
      setError("The number and the date cannot be cleared.");
      return;
    }
    if (date && date > today) {
      setError(`A ${paperWord} cannot be dated after today.`);
      return;
    }
    let typedRates: Array<{ ratePercent: number; baseEur: number; ivaEur: number }> | undefined;
    if (moneyTouched) {
      typedRates = [];
      for (const row of rates) {
        const rate = parseRate(row.rate);
        if (rate === null) {
          setError("Type each IVA rate, e.g. 21.");
          return;
        }
        const base = parseTypedAmount(row.base);
        const iva = parseTypedAmount(row.iva);
        if (!base.ok || !iva.ok) {
          setError(!base.ok ? `Base: ${base.message}` : `IVA: ${iva.ok ? "" : iva.message}`);
          return;
        }
        typedRates.push({ ratePercent: rate, baseEur: base.value, ivaEur: iva.value });
      }
    }
    const changes = {
      ...(nextNumber && nextNumber !== (invoice.invoiceNumber ?? "") ? { invoiceNumber: nextNumber } : {}),
      ...(date && date !== savedDate ? { invoiceDate: date } : {}),
      ...(typedRates ? { rates: typedRates } : {}),
    };
    if (Object.keys(changes).length === 0) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.typeFacturaDetails(invoice.id, changes);
      onChanged();
      setEditing(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : `Could not save the ${paperWord}'s details`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inv-paper-use">
      <div className="inv-paper-use-head">
        <strong>{credit ? "Credit note" : "Factura"}</strong>
        <span>{invoice.invoiceNumber ?? "no number"}</span>
        <span className="muted">·</span>
        <span>{savedDate ? calendarDayText(savedDate) : "no date"}</span>
        <span className="muted">·</span>
        <span>IVA {invoice.ivaEur !== null ? money(invoice.ivaEur) : "not read"}</span>
        {notCounted ? <span className="badge warning">{notCounted}</span> : null}
      </div>
      {error ? <div className="alert error">{error}</div> : null}
      {editing ? (
        <>
          <div className="field">
            <label htmlFor={`factura-number-${invoice.id}`}>Number</label>
            <input
              id={`factura-number-${invoice.id}`}
              value={number}
              maxLength={100}
              disabled={busy}
              onChange={(e) => setNumber(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor={`factura-date-${invoice.id}`}>Date</label>
            <input
              id={`factura-date-${invoice.id}`}
              type="date"
              max={today}
              value={date}
              disabled={busy}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="factura-rates" role="group" aria-label="IVA by rate">
            {rates.map((row, i) => (
              <div key={row.key} className="factura-rate-row">
                <div className="field">
                  <label htmlFor={`factura-rate-${row.key}`}>IVA %</label>
                  <input
                    id={`factura-rate-${row.key}`}
                    inputMode="decimal"
                    value={row.rate}
                    disabled={busy}
                    onChange={(e) => patchRate(row.key, { rate: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor={`factura-base-${row.key}`}>{credit ? "Base credited (before IVA)" : "Base (before IVA)"}</label>
                  <input
                    id={`factura-base-${row.key}`}
                    inputMode="decimal"
                    value={row.base}
                    disabled={busy}
                    onChange={(e) => patchRate(row.key, { base: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor={`factura-iva-${row.key}`}>{credit ? "IVA credited" : "IVA"}</label>
                  <input
                    id={`factura-iva-${row.key}`}
                    inputMode="decimal"
                    value={row.iva}
                    disabled={busy}
                    onChange={(e) => patchRate(row.key, { iva: e.target.value })}
                  />
                </div>
                {rates.length > 1 ? (
                  <button
                    type="button"
                    className="button plain"
                    disabled={busy}
                    aria-label={`Remove rate ${i + 1}`}
                    onClick={() => {
                      setMoneyTouched(true);
                      setRates((rows) => rows.filter((r) => r.key !== row.key));
                    }}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            ))}
            {/* The server's MAX_TYPED_RATES (`paper-rules.ts`); it refuses a fifth. */}
            {rates.length < 4 ? (
              <button
                type="button"
                className="button plain"
                disabled={busy}
                onClick={() => {
                  setMoneyTouched(true);
                  setRates((rows) => [...rows, blankRate()]);
                }}
              >
                Add a rate
              </button>
            ) : null}
          </div>
          <div className="button-row">
            <button type="button" className="button" disabled={busy} onClick={() => void save()}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button type="button" className="button plain" disabled={busy} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <div className="button-row">
          <button type="button" className="button tinted" onClick={open}>
            {complete ? `Correct the ${paperWord}'s details` : "Type what is missing"}
          </button>
        </div>
      )}
    </div>
  );
}
